<script setup>
import DOMPurify from 'dompurify';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { dateHelper } from '@shop/frontend/web/js/helpers/dateHelper';
import { helpers } from '@shop/frontend/web/js/helpers';
import { ref, computed } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ru';

dayjs.extend(utc);
dayjs.locale('ru');

const DEFAULT_ICON_URL = "/images/site/logo-top.svg";

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  isShort: {
    type: Boolean,
    default: false,
  },
  isPerfomance: {
    type: Boolean,
    default: false,
  },
  isPackage: {
    type: Boolean,
    default: false,
  },
  isPackageItem: {
    type: Boolean,
    default: false,
  },
});

const showDescription = ref(false);

const matchesInfoText = computed(() => {
  const matchesCorrectForm = helpers.wordEnding(props.event.eventsCount, [
    translator.t('event', 'match'),
    translator.t('event', 'matches'),
    translator.t('event', 'matches '),
  ]);

  return `${props.event.eventsCount} ${matchesCorrectForm}`;
});

const sanitizedEventDescription = computed(() => DOMPurify.sanitize(props.event.description));

const isSubscription = computed(() => props.event.eventType === 'Абонемент' || props.event.eventType === 'Наборный')
const buttonText = computed(() => {
  const buttonLabel = props.event?.hasNonZeroPrices === false
    ? 'Get a ticket'
    : 'Buy a ticket';

  return translator.t('main', buttonLabel);
});

</script>

<template>
  <div>
    <div class="i-event-item__nomenclature" v-if="event?.settings?.isShowSeason && !isPackageItem">
      {{ event?.nom?.name }}
    </div>
    <div class="i-event-item" :class="{
      'i-event-item_short': isShort,
      'i-event-item_perfomance': isPerfomance,
      'i-event-item_soon': event.settings && event.settings.isSoon,
      'i-event-item_long': showDescription,
      'i-event-item__package-item': isPackageItem
    }">
      <div class="i-event-item__wrapper" :class="{
        'i-event-item__wrapper_perfomance': isPerfomance,
        'i-event-item__wrapper_subscription': isSubscription,
      }">
        <div v-if="!isShort && !isSubscription" class="i-event-item-info" :class="{
          'i-event-item-info_perfomance': isPerfomance,
        }">
          <slot name="info">
            <div v-if="event.eventsCount && isPackage" class="i-event-item-info__matches">
              {{ matchesInfoText }}
            </div>

            <div v-if="event.settings.isShowDate && !isPackage"
              class="d-inline-flex align-items-center i-event-item-info__date">
              <span>
                {{ dayjs(event.startTimestampStr).format('DD MMMM, dddd HH:mm') }}
              </span>
            </div>
          </slot>
        </div>
        <slot name="name">
          <component :is="!event.settings.isSoon ? 'a' : 'div'" v-if="event.participants && event.participants.singular"
            v-bind="!event.settings.isSoon ? { href: event.pageLink } : {}" class="i-event-item-logo-name" :class="{
              'i-event-item-logo-name__right': isSubscription
            }">
            <img class="i-event-item__logo" :src="event.participants.singular.imageUrl || DEFAULT_ICON_URL" alt=""
              width="50" height="50">
            <div class="i-event-item__package-name-wrapper">
              <span class="i-event-item__name">
                {{ event.participants.singular.name }}
              </span>
              <span v-if="event?.nom?.name" class="i-event-item__nom-name">
                {{ event?.nom?.name }}
              </span>
            </div>
          </component>
          <component v-else-if="isPackage" :is="!event.settings.isSoon ? 'a' : 'div'"
            v-bind="!event.settings.isSoon ? { href: event.pageLink } : {}" class="i-event-item-logo-name">
            <img class="i-event-item__logo"
              :src="event?.participants?.team?.homeTeam?.imageUrl || event?.packageEvents?.[0]?.participants?.team?.homeTeam?.imageUrl || DEFAULT_ICON_URL"
              alt="" width="50" height="50">
            <div class="i-event-item__package-name-wrapper">
              <span class="i-event-item__name_package">
                {{ event.name }}
              </span>
              <span v-if="event?.nomName" class="i-event-item__nom-name">
                {{ event.nomName }}
              </span>
            </div>
          </component>
        </slot>
        <div class="i-event-item-actions">
          <slot name="actions">
            <button v-if="(event.description || event.eventType === 'Пакет') && !isPackageItem" type="button"
              class="btn btn_secondary-border i-event-item-actions__btn-details"
              @click="showDescription = !showDescription">
              {{
                showDescription
                ? translator.t("main", "Hide")
                : translator.t("main", "More details")
              }}
            </button>

            <a v-if="event.settings && event.settings.parkingId && !isPackageItem"
              :href="`/event?id_event=${event.settings.parkingId}`" data-test-id="parking_btn"
              class="btn_secondary js-hover-out">
              <svg width="20" height="20" class="icon icon_cart" aria-hidden="true" focusable="false">
                <use xlink:href="#parking" />
              </svg>
              <span class="sr-only">
                {{ translator.t("main", "Go to the event page") }}
              </span>
            </a>

            <a v-if="!(event.settings && event.settings.isSoon) && !isPackageItem" :href="event.pageLink"
              :title="translator.t('main', 'Go to the event page')"
              class="btn btn_secondary i-event-item-actions__btn js-hover-out">
              {{ buttonText }}
              <span class="sr-only">
                {{ translator.t("main", "Go to the event page") }}
              </span>
            </a>
            <span v-else-if="event.settings && event.settings.isSoon && !isPackageItem"
              class="i-event-item__coming-soon i-event-item-actions__btn">
              {{ translator.t('main', 'Coming soon') }}
            </span>
          </slot>
        </div>
      </div>
      <slot v-if="showDescription && isPackage" name="package" />
      <div v-if="event.description && showDescription" class="i-event-item-expand" :class="{ 'd-block': showDescription }"
        v-html="sanitizedEventDescription" />
    </div>
  </div>
</template>

<style lang="scss">
.i-event-item {
  border-radius: 10px;
  background-image:
    url('../../../../images/site/pattern-left.svg'),
    url('../../../../images/site/pattern-right.svg');
  background-repeat: no-repeat, no-repeat;
  background-position:
    left center,
    right center;
  background-size:
    50% 100%,
    50% 100%;
  background-color: #F6F6F6;
  border: none;
  padding: 20px;
  min-height: 90px;
  position: relative;

  &_long {
    background-image:
      url('../../../../images/site/pattern-left_long.svg'),
      url('../../../../images/site/pattern-right_long.svg');
    background-repeat: no-repeat, no-repeat;
    background-position:
      left center,
      right center;
    background-size:
      50% 100%,
      50% 100%;
  }

  &_short {
    background-image: url('../../../../images/site/pattern-short.svg');
    background-position: right center;
    background-size: 60% 100%;

    .i-event-item-actions {
      min-width: 133px;
      flex-direction: column;
    }

    .i-event-item-actions__btn {
      min-width: 133px;
      padding: 13px 15px;
    }
  }

  &_perfomance {
    background-image: url('../../../../images/site/pattern-short.svg');
    background-position: right center;
    background-size: 30% 100%;
    background-repeat: no-repeat;

    .i-event-item__age-limit {
      margin-right: 16px;
    }
  }

  &__image {
    position: relative;
    height: 113px;
    margin-right: 12px;
  }

  &-actions {
    flex-wrap: nowrap;
  }

  &__nomenclature {
    padding: 8px 10px;
    background: $primary;
    border-radius: 5px 5px 0px 0px;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: $white;
    text-align: center;

    &+.i-event-item {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  &_soon {
    border-top: none;
    border-radius: 0 0 20px 20px;
  }

  &__soon-block {
    text-align: center;
    border-radius: 15px 15px 0 0;
    padding: 3px 0;
    width: 100%;
    font-weight: 500;
    font-size: 16px;
  }

  &__package-name-wrapper {
    display: flex;
    flex-direction: column;
    margin-left: 12px;
  }

  &__nom-name {
    font-size: 14px;
    color: $gray-medium;
  }

  &__name_package {
    font-size: 18px;
    font-weight: 700;
    font-family: 'Oswald', sans-serif;
  }

  &__coming-soon {
    height: 44px;
    padding: 12px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $white;
    background-color: $primary;
    border-radius: 50px;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    text-align: center;
  }

  &__pushkin-icon {
    position: absolute;
    top: 6px;
    left: 6px;
    color: $white;
    background-color: $additional;
    padding: 4.5px 6.5px;
    border-radius: 4px;
  }

  &__info-wrapper {
    margin-bottom: 12px;
  }

  &__age-limit {
    background-color: $grey-light;
    padding: 4px 6px;
  }

  &__name {
    font-family: 'Oswald', sans-serif;
    font-size: 20px;
    line-height: 130%;
    margin-bottom: 8px;
  }

  &__min-price {
    font-size: 16px;
  }

  &__wrapper {
    &_perfomance {
      .i-event-item__name {
        margin-bottom: 0;
      }
    }

    &_subscription {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  &__package-item {
    padding: 8px 12px;
    background-image: none;
    background-color: transparent;

    .i-event-item-info {
      margin-right: 0;
    }
  }

  &:not(.i-event-item_short) {
    .i-event-item-info {
      min-width: 305px;
      max-width: 305px;
    }

    .i-event-item-actions {
      min-width: 323px;
      max-width: 323px;
    }
  }

  @media (max-width: $screen-lg-min) {
    &:not(.i-event-item_short) {
      .i-event-item-info {
        min-width: 205px;
        max-width: 205px;
      }
    }
  }

  @media (max-width: $screen-lg-sm) {
    &:not(.i-event-item_short) {
      .i-event-item-info {
        min-width: 160px;
        max-width: 160px;
      }
    }
  }

  @media (max-width: $screen-md) {
    padding: 16px;

    &:not(.i-event-item_short, .i-event-item_perfomance, .i-event-item__package-item) {
      background-image:
        url('../../../../images/site/pattern-left_mobile.svg'),
        url('../../../../images/site/pattern-right_mobile.svg');
      background-position: top left, top right;
      background-size: 50%;
      background-repeat: no-repeat, no-repeat;
    }

    &:not(.i-event-item_short) {
      .i-event-item-actions {
        min-width: 300px;
        max-width: 300px;

        &__btn {
          min-width: 100px;
        }
      }
    }

    &__image {
      width: 97px;
      min-width: 97px;
      height: 130px;
    }

    &__name {
      font-size: 18px;
    }

    &__min-price {
      font-size: 14px;
    }

    &__perfomance-info-right {
      align-items: flex-start;
      text-align: left;
    }

    &_short {
      background-image: url('../../../../images/site/pattern-short_mobile.svg');
      background-position: right top;
      background-size: 100%;

      .i-event-item-actions {
        flex-direction: row;
      }
    }

    &_perfomance {
      background-image: url('../../../../images/site/pattern-short_mobile.svg');
      background-position: right top;
      background-size: 100%;
      background-repeat: no-repeat;

      .i-event-item__age-limit {
        width: fit-content;
      }
    }

    &_long {
      &:not(.i-event-item_short, .i-event-item_perfomance) {
        background-image:
          url('../../../../images/site/pattern-left_mobile.svg'),
          url('../../../../images/site/pattern-right_mobile.svg');
        background-position: top left, top right;
      }
    }
  }

  @media (max-width: $screen-md-min) {
    &:not(.i-event-item_short) {
      .i-event-item-info {
        min-width: 100%;
        max-width: 100%;
      }

      .i-event-item-actions {
        min-width: 100%;
        max-width: 100%;
      }
    }

    &__wrapper {
      &_subscription {
        display: flex;
      }
    }

    &__package-name-wrapper {
      gap: 6px;
      align-items: center;
      text-align: center;
      margin-left: 0;
    }
  }


  @media (max-width: $screen-xs) {
    &__perfomance-info-wrapper {
      width: auto;
    }

    &__wrapper {
      &_perfomance {
        align-items: center;
      }
    }
  }

  @media (max-width: $screen-xs-min) {
    &__perfomance-info-wrapper {
      width: 100%;
    }
  }
}

.i-event-item-logo-name {
  &__right {
    grid-column: 2;
    justify-content: center;
  }

  @media (max-width: $screen-md-min) {
    flex-direction: column;
    gap: 8px;
  }
}

.i-event-item-logo-name .i-event-item__name {
  position: relative;
  left: 0;
  width: auto;
  margin-left: 0;

  @media (max-width: $screen-md-min) {
    margin-bottom: 0;
  }
}

.i-event-item-info {
  font-size: 14px;
  gap: 9px;

  &__matches {
    color: $white;
    background-color: $primary;
    padding: 10px 18px;
    margin-bottom: 0;
  }

  &__date {
    font-weight: 600;
  }

  &__location {
    color: $grey-second;
    font-weight: 500;

    svg {
      min-width: 20px;
    }
  }

  &__date-wrapper {
    font-size: 14px;
    gap: 8px;
    margin-top: 12px;
  }

  @media (max-width: $screen-xs) {
    &__matches {
      border-radius: 50px;
      padding: 8px 12px;
      position: absolute;
      top: 12px;
      left: 12px;
    }

    &__date-wrapper {
      margin-top: 0;
    }
  }
}

.i-event-item-expand {
  background-color: rgba(197, 196, 196, 0.07);
  backdrop-filter: blur(11.6px);
  border: none;
  padding: 8px 12px;
}

.i-event-item-actions {
  &__btn {
    min-width: 170px;
  }

  &__btn-details {
    min-width: 133px;
  }

  @media (max-width: $screen-xs) {

    &__btn,
    &__btn-details {
      min-width: auto;
      width: 50%;
      padding: 12px;
    }
  }
}
</style>
